<template>
    <div>
        <StylisedModal ref="modal" :title="modalTitle" @closed="reset()">
            <LoadingSpinner v-if="isLoading" class="my-5" />
            <div class="view-invoice-content">
                <template v-if="invoice">
                    <small
                        v-if="invoice.supplier_raised"
                        class="d-block w-100 py-1 text-center mb-2"
                        :class="{
                            'bg-success text-white': invoice.status === 'approved',
                            'bg-warning text-white': invoice.status === 'awaiting_approval',
                            'bg-danger text-white': invoice.status === 'rejected',
                        }"
                    >
                        <template v-if="invoice.status === 'approved'">
                            <template v-if="invoice.integrator === 'acumatica'">
                                This invoice was imported from Acumatica and was approved automatically
                            </template>
                            <template v-else-if="invoice.approver">
                                This invoice was raised by the supplier and has been approved by
                                {{ invoice.approver.name }}
                            </template>
                            <template v-else>This invoice was approved automatically on import</template>
                        </template>
                        <template v-else-if="invoice.status === 'awaiting_approval'">
                            <template v-if="invoice.integrator === 'acumatica'">
                                This invoice was imported from Acumatica and is awaiting approval
                            </template>
                            <template v-else>This invoice was raised by the supplier and is awaiting approval</template>
                        </template>
                        <template v-else-if="invoice.status === 'rejected'">
                            <template v-if="invoice.integrator === 'acumatica'">
                                This invoice was imported from Acumatica and was rejected
                                <template v-if="invoice.approver">by {{ invoice.approver.name }}</template>
                            </template>
                            <template v-else>
                                This invoice was rejected
                                <template v-if="invoice.approver">by {{ invoice.approver.name }}</template>
                                <template v-else>during import</template>
                            </template>
                            <span v-if="invoice.approval_comments">
                                with the following comments:
                                <i>{{ invoice.approval_comments }}</i>
                            </span>
                        </template>
                    </small>
                    <div class="invoice-heading pad-x">
                        <FormField label="Title">
                            <h4><EditableText v-model="invoice.title" :editable="isEditable" :max-length="255" /></h4>
                        </FormField>
                        <FormField label="Invoice Number" class="tw-text-right">
                            <h4 class="text-muted">
                                <EditableText
                                    v-model="invoice.invoice_number"
                                    :editable="isEditable"
                                    :max-length="255"
                                />
                            </h4>
                        </FormField>
                    </div>
                    <div class="row pad-x">
                        <div class="col-6 mb-3">
                            <FormField label="Invoice Date">
                                <VueFlatPickr
                                    v-model="invoice.invoice_date"
                                    placeholder="Required"
                                    :config="flatpickrConfig"
                                    :disabled="!isEditable"
                                    class="input is-small"
                                    required
                                />
                            </FormField>
                        </div>
                        <div class="col-6 mb-3">
                            <FormField label="Due Date">
                                <VueFlatPickr
                                    v-model="invoice.due_date"
                                    placeholder="Required"
                                    :config="flatpickrConfig"
                                    :disabled="!isEditable"
                                    class="input is-small"
                                    required
                                />
                            </FormField>
                        </div>
                        <div class="col-6">
                            <FormField :label="invoiceType === 'CustomerInvoice' ? 'Customer' : 'Supplier'">
                                <User :user="invoice.supplier" :hide-name="false" size="xsmall">
                                    <template #default="{ user: supplier }">
                                        <div class="tw-flex tw-items-center tw-gap-2">
                                            {{ supplier.name }}
                                            <img
                                                v-if="isConnectedToXero"
                                                src="@/images/brands/xero_blue.png"
                                                class="tw-w-5 tw-h-5"
                                                :class="{
                                                    'tw-grayscale': !supplier.xero_contact_id,
                                                }"
                                                :alt="
                                                    supplier.xero_contact_id
                                                        ? 'Connected to Xero'
                                                        : 'Not connected to Xero'
                                                "
                                            />
                                            <img
                                                v-if="isConnectedToAcumatica"
                                                src="@/images/brands/acumatica-icon.svg"
                                                class="tw-w-5 tw-h-5"
                                                :class="{
                                                    'tw-grayscale': !supplier.acumatica_contact_id,
                                                }"
                                                :alt="
                                                    supplier.acumatica_contact_id
                                                        ? 'Connected to Acumatica'
                                                        : 'Not connected to Acumatica'
                                                "
                                            />
                                        </div>
                                    </template>
                                </User>
                            </FormField>
                        </div>
                        <div class="col-6">
                            <FormField label="Created by">
                                <div
                                    v-if="invoice.integrator === 'acumatica' && !invoice.created_by"
                                    class="tw-flex tw-min-w-0 tw-items-center tw-gap-2"
                                >
                                    <img
                                        src="@/images/brands/acumatica-icon.svg"
                                        alt="Acumatica"
                                        class="tw-h-7 tw-w-7 tw-shrink-0 tw-object-contain"
                                    />
                                    <span class="tw-truncate tw-text-sm tw-leading-5">Acumatica</span>
                                </div>
                                <User v-else :user="invoice.created_by" :hide-name="false" size="xsmall" />
                            </FormField>
                        </div>
                        <div
                            v-if="invoiceType === 'CustomerInvoice' && (isEditable || invoice.payment_terms)"
                            class="col-12 pt-3"
                        >
                            <FormField class="tw-flex-1 tw-pb-3" label="Purchase Order Number">
                                <Textbox
                                    v-model="invoice.po_number"
                                    placeholder="Customers PO number..."
                                    class="tw-w-full"
                                />
                            </FormField>

                            <FormField label="Custom Payment Terms">
                                <div class="tw-flex tw-gap-2">
                                    <Textarea
                                        v-model="paymentTermsDraft"
                                        placeholder="Optional"
                                        :disabled="!isEditable"
                                        expandable
                                    />
                                    <Button
                                        v-if="isEditable"
                                        type="is-success"
                                        :disabled="invoice.payment_terms == paymentTermsDraft"
                                        @click="invoice.payment_terms = paymentTermsDraft"
                                    >
                                        Save
                                    </Button>
                                </div>
                            </FormField>
                        </div>
                    </div>
                    <hr class="tw-mt-3 mb-0" />
                    <Tabs :tabs="visibleTabs">
                        <template #tab(details)>
                            <table class="invoice-line-items-table !tw-mb-3">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th class="tw-text-right">Unit Price</th>
                                        <th class="tw-text-right">Tax Rate</th>
                                        <th class="tw-text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="lineItem in invoice.line_items" :key="lineItem.id">
                                        <td>{{ lineItem.description }}</td>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{
                                                    formatCurrency(
                                                        lineItem.unit_price_target,
                                                        2,
                                                        invoice.target_currency
                                                    )
                                                }}
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(lineItem.unit_price, 2, event?.currency_code) }}
                                            </span>
                                        </td>
                                        <td
                                            class="tw-text-right"
                                            :class="{ 'text-muted': !lineItem.tax_rate || !lineItem.tax_rate.rate }"
                                        >
                                            {{ lineItem.tax_rate ? lineItem.tax_rate.rate + '%' : 'None' }}
                                        </td>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{ formatCurrency(lineItem.value_target, 2, invoice.target_currency) }}
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(lineItem.subtotal, 2, event?.currency_code) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <th colspan="2"></th>
                                        <th class="tw-text-right">Subtotal</th>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{
                                                    formatCurrency(invoice.subtotal_target, 2, invoice.target_currency)
                                                }}
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(invoice.subtotal, 2, event?.currency_code) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th colspan="2"></th>
                                        <th class="tw-text-right">Tax</th>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{
                                                    formatCurrency(
                                                        invoice.tax_amount_target,
                                                        2,
                                                        invoice.target_currency
                                                    )
                                                }}
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(invoice.tax_amount, 2, event?.currency_code) }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th colspan="2"></th>
                                        <th class="tw-text-right">Total</th>
                                        <td class="tw-text-right">
                                            {{
                                                formatCurrency(
                                                    invoice.total_value_target
                                                        ? invoice.total_value_target
                                                        : invoice.total_value,
                                                    2,
                                                    invoice.target_currency
                                                )
                                            }}
                                        </td>
                                    </tr>
                                    <tr v-if="invoice.total_paid || invoice.total_credit">
                                        <th colspan="2"></th>
                                        <th class="tw-text-right">Less Payments/Credits</th>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{
                                                    formatCurrency(
                                                        parseFloat(
                                                            invoice.total_paid_target ? invoice.total_paid_target : 0
                                                        ) +
                                                            parseFloat(
                                                                invoice.total_credit_target
                                                                    ? invoice.total_credit_target
                                                                    : 0
                                                            ),
                                                        2,
                                                        invoice.target_currency
                                                    )
                                                }}
                                            </span>
                                            <span v-else>
                                                -{{
                                                    formatCurrency(
                                                        parseFloat(invoice.total_paid ? invoice.total_paid : 0) +
                                                            parseFloat(invoice.total_credit ? invoice.total_credit : 0),
                                                        2,
                                                        event?.currency_code
                                                    )
                                                }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th colspan="2"></th>
                                        <th class="tw-text-right">Outstanding</th>
                                        <td class="tw-text-right">
                                            <span v-if="invoice.target_currency">
                                                {{ formatCurrency(totalOutstanding, 2, invoice.target_currency) }}
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(totalOutstanding, 2, event?.currency_code) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                            <Banner
                                v-if="invoice.base_currency != invoice.target_currency"
                                type="is-info"
                                class="tw-m-2 tw-text-xs"
                                :closable="false"
                            >
                                The target currency of this invoice ({{ invoice.target_currency }}) differs from the
                                event currency ({{ invoice.base_currency }}).
                                <br />
                                The implied value of this invoice is
                                {{ formatCurrency(impliedInvoiceValue, 2, invoice.base_currency) }}
                                based on a conversion rate of {{ invoice.conversion_rate }}.
                            </Banner>
                        </template>
                        <template #heading(payments)>
                            Payments/Credits
                            <span class="badge bg-info text-white ml-1">
                                {{ invoice.combined_payments.length }}
                            </span>
                        </template>
                        <template #tab(payments)>
                            <table class="invoice-line-items-table !tw-mb-3 tw-align-middle">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Date</th>
                                        <th>Status</th>
                                        <th class="text-right">Amount</th>
                                        <th class="text-right"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(payment, index) in invoice.combined_payments" :key="index">
                                        <td>
                                            {{
                                                payment.type === 'credit_note'
                                                    ? 'Credit Note'
                                                    : payment.type === 'overpayment'
                                                      ? 'Overpayment'
                                                      : 'Payment'
                                            }}
                                        </td>
                                        <td>{{ DateTime.fromISO(payment.date).toLocaleString(DateTime.DATE_FULL) }}</td>
                                        <td>
                                            <Tag v-if="payment.type === 'credit_note'" type="warning">Credit</Tag>
                                            <Tag v-else-if="payment.is_overdue && !payment.is_paid" type="danger">
                                                Overdue
                                            </Tag>
                                            <Tag v-else-if="payment.is_paid" type="success">Paid</Tag>
                                            <Tag v-else type="info">Scheduled</Tag>
                                        </td>
                                        <td class="text-right">
                                            <span v-if="invoice.target_currency !== invoice.base_currency">
                                                {{ formatCurrency(payment.amount_target, 2, invoice.target_currency) }}
                                                ({{ formatCurrency(payment.amount, 2, invoice.base_currency) }})
                                            </span>
                                            <span v-else>
                                                {{ formatCurrency(payment.amount, 2, invoice.base_currency) }}
                                            </span>
                                        </td>
                                        <td class="!tw-text-end">
                                            <button
                                                v-if="canManagePayments && payment.type === 'credit_note'"
                                                @click="deleteCreditNote(payment)"
                                            >
                                                <i class="mdi mdi-close-thick tw-text-red-400 hover:tw-text-red-500" />
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </template>
                        <template #heading(activity)>
                            Activity
                            <span class="badge bg-info text-white ml-1">{{ invoice.activity.length }}</span>
                        </template>
                        <template #tab(activity)>
                            <div class="tw-max-h-[32rem] tw-overflow-y-auto">
                                <Activity :activity="invoice.activity" />
                            </div>
                        </template>
                        <template #heading(purchase_orders)>
                            Purchase Orders
                            <span class="badge bg-info text-white ml-1">{{ invoice.purchase_orders.length }}</span>
                        </template>
                        <template #tab(purchase_orders)>
                            <table class="invoice-line-items-table tw-mb-3">
                                <thead>
                                    <tr>
                                        <th>Reference</th>
                                        <th>Title</th>
                                        <th class="text-right w-10">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="poItem in invoice.purchase_orders" :key="poItem.id">
                                        <!-- @click.prevent="viewPurchaseOrder(poItem.id)" -->
                                        <td>
                                            <a href="#" @click.prevent="viewPurchaseOrder(poItem)">
                                                {{ poItem.reference }}
                                            </a>
                                        </td>
                                        <td>{{ poItem.title }}</td>
                                        <td class="text-right w-10">{{ localise(poItem.subtotal, 2, true) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </template>

                        <template #heading(attachments)>Attachments</template>
                        <template #tab(attachments)>
                            <div
                                v-if="invoice.file_uploads?.length"
                                class="tw-divide-y tw-divide-gray-100 tw-border-b tw-border-gray-100"
                            >
                                <div
                                    v-for="(file, index) in invoice.file_uploads"
                                    :key="index"
                                    class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-2 hover:tw-bg-gray-50"
                                >
                                    <span class="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                                        <i class="mdi mdi-file-document-outline tw-flex-shrink-0 tw-text-gray-400"></i>
                                        <span class="tw-truncate tw-text-sm tw-text-gray-800">
                                            {{ file.file_name }}
                                        </span>
                                    </span>
                                    <div class="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1">
                                        <Tooltip label="Download" position="top">
                                            <Button compact square @click="openAttachment(file)">
                                                <i class="mdi mdi-download"></i>
                                            </Button>
                                        </Tooltip>
                                        <template v-if="isConnectedToXero && isPublishedToXero">
                                            <Tooltip
                                                v-if="fileXeroIntegration(file)"
                                                label="Uploaded to Xero"
                                                position="top"
                                            >
                                                <Button compact square disabled>
                                                    <i class="mdi mdi-check tw-font-bold"></i>
                                                </Button>
                                            </Tooltip>
                                            <Tooltip v-else label="Push to Xero" position="top">
                                                <Button
                                                    compact
                                                    square
                                                    type="is-xero"
                                                    :loading="pushingToXero[file.id]"
                                                    @click="pushAttachmentToXero(file)"
                                                >
                                                    <i class="mdi mdi-cloud-upload"></i>
                                                </Button>
                                            </Tooltip>
                                        </template>
                                        <Tooltip label="Remove attachment" position="top">
                                            <Button compact square type="is-danger" @click="removeAttachment(index)">
                                                <i class="mdi mdi-delete-outline"></i>
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                            <div class="tw-p-4">
                                <Upload
                                    :accept="[
                                        'application/pdf',
                                        '.xlsx',
                                        '.doc',
                                        '.docx',
                                        'application/msword',
                                        'application/vnd.ms-excel',
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                        'image/*',
                                    ]"
                                    multiple
                                    @update:model-value="(file) => updateFilesChanged(file)"
                                />
                            </div>
                        </template>
                    </Tabs>
                    <div
                        v-if="
                            invoice.supplier_raised &&
                            invoice.status === 'awaiting_approval' &&
                            Auth.can('authorise supplier invoices')
                        "
                        class="row px-4 mb-3"
                    >
                        <div v-if="invoice.file_uploads && invoice.file_uploads.length" class="col-12 pb-3">
                            <FormField label="Attached Files">
                                <a
                                    v-for="file in invoice.file_uploads"
                                    :key="file.id"
                                    :href="file.download_url"
                                    target="_blank"
                                    class="d-block"
                                >
                                    {{ file.file_name ? file.file_name : 'Untitled File' }}
                                </a>
                            </FormField>
                        </div>
                        <div class="col-6">
                            <Button type="is-success" class="tw-w-full" @click="authoriseInvoice()">Authorise</Button>
                        </div>
                        <div class="col-6">
                            <Button type="is-danger" class="tw-w-full" @click="rejectInvoice()">Reject</Button>
                        </div>
                    </div>
                </template>
            </div>
            <template
                v-if="
                    invoice &&
                    !currentAccount.is_supplier &&
                    (invoice.status === 'approved' || invoiceType === 'CustomerInvoice') &&
                    (isEditable ||
                        invoiceType === 'CustomerInvoice' ||
                        isPublishedToAcumatica ||
                        canManagePayments ||
                        canDeleteInvoice)
                "
                #actions
            >
                <a v-if="isPublishedToAcumatica" :href="acumaticaInvoiceUrl" target="_blank">View in Acumatica</a>
                <a v-if="isPublishedToXero" :href="invoice.view_url" target="_blank">View in Xero</a>
                <a
                    v-if="isPublishedToXero"
                    href="#"
                    class="tw-mt-1"
                    :class="{ 'text-muted': isResyncing }"
                    @click.prevent="isResyncing ? null : resyncFromXero()"
                >
                    {{ isResyncing ? 'Resyncing...' : 'Resync from Xero' }}
                </a>
                <a
                    v-else-if="canPublishToXero"
                    :class="{
                        'text-muted': isPublishing,
                    }"
                    href="#"
                    @click.prevent="isPublishing ? null : publishToXero()"
                >
                    {{ isPublishing ? 'Publishing to Xero...' : 'Publish to Xero' }}
                </a>
                <a
                    v-if="invoiceType === 'CustomerInvoice'"
                    href="#"
                    target="_blank"
                    class="mt-1"
                    @click.prevent="downloadInvoice()"
                >
                    Download invoice (PDF)
                </a>
                <a
                    v-if="invoiceType === 'CustomerInvoice'"
                    href="#"
                    class="mt-1"
                    @click.prevent="showEmailInvoiceModal()"
                >
                    Send invoice by email
                </a>
                <a
                    v-if="canManagePayments"
                    href="#"
                    :class="
                        invoiceType === 'CustomerInvoice' || isPublishedToXero || isPublishedToAcumatica ? 'mt-2' : ''
                    "
                    @click.prevent="managePayments()"
                >
                    Manage payment dates
                </a>
                <a v-if="canManagePayments" href="#" class="tw-mt-1" @click.prevent="raiseCreditNote()">
                    Raise a credit note
                </a>
                <a v-if="canDeleteInvoice" href="#" class="tw-mt-2 text-danger" @click.prevent="deleteInvoice()">
                    Delete invoice
                </a>
            </template>
        </StylisedModal>
        <InvoicePaymentDates ref="invoicePaymentDatesModal" @closed="reload()" />
        <RaiseCreditNote />
        <AlterCurrency ref="alterCurrencyModal" @updated="updateCurrency" />
        <DeleteInvoice ref="deleteInvoiceModal" @deleted="close" />
    </div>
</template>
<script>
import StylisedModal from '@/js/components/StylisedModal.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import User from '@/js/components/widgets/User.vue';
import InvoicePaymentDates from '@/js/components/modals/Invoices/InvoicePaymentDates.vue';
import DeleteInvoice from '@/js/components/modals/Invoices/DeleteInvoice.vue';
import RaiseCreditNote from '@/js/components/modals/Invoices/RaiseCreditNote.vue';
import AlterCurrency from '@/js/components/modals/AlterCurrency.vue';
import Button from '@/js/components/controls/Button.vue';
import Activity from '@/assets/js/eventbooks/components/drawer-tabs/activity.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import debounce from 'lodash/debounce';
import FormField from '@/js/components/widgets/FormField.vue';
import Banner from '@/js/components/Layout/Banner.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import Tag from '@/js/components/Tag.vue';
import { DateTime } from 'luxon';
import { toast, obj2form, formatCurrency } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import { subtotal, total } from '@/js/composables/useLineItemTotals.js';
import { totalOutstanding as calculateTotalOutstanding } from '@/js/utils/invoiceTotals.js';
import Upload from '@/js/components/controls/Upload.vue';
import VueFlatPickr from 'vue-flatpickr-component';
import Textarea from '@/js/components/controls/Textarea.vue';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import Textbox from '@/js/components/controls/Textbox.vue';

export default {
    components: {
        Textbox,
        Activity,
        Upload,
        StylisedModal,
        EditableText,
        User,
        InvoicePaymentDates,
        DeleteInvoice,
        RaiseCreditNote,
        AlterCurrency,
        Button,
        FormField,
        Banner,
        Tabs,
        Tooltip,
        Textarea,
        Tag,
        VueFlatPickr,
        LoadingSpinner,
    },
    data() {
        const store = useDataStore();

        return {
            isLoading: false,
            isUpdatingFiles: false,
            isPublishing: false,
            isResyncing: false,
            pushingToXero: {},
            invoiceType: null,
            invoice: null,
            currentAccount: store.get('account'),
            store: store,
            routeData: null,
            DateTime,
            paymentTermsDraft: '',
        };
    },
    computed: {
        event() {
            if (this.routeData?.event) {
                return this.store.get(`account.events.${this.routeData.event}`);
            }
            const storeEvent = this.store.get('current_event');
            if (storeEvent) {
                return storeEvent;
            }
            return null;
        },
        modalTitle() {
            if (!this.invoiceType) {
                return 'View Invoice';
            }
            return this.invoiceType === 'CustomerInvoice' ? 'View Customer Invoice' : 'View Supplier Invoice';
        },
        isEditable() {
            return (
                Auth.can(
                    this.invoiceType === 'CustomerInvoice' ? 'update customer invoices' : 'update supplier invoices'
                ) &&
                !this.event?.is_closed &&
                !this.isAcumaticaInvoice
            );
        },
        canManagePayments() {
            return (
                Auth.can(
                    this.invoiceType === 'CustomerInvoice'
                        ? 'manage customer invoice payments'
                        : 'manage supplier invoice payments'
                ) && !this.event?.is_closed
            );
        },
        canDeleteInvoice() {
            return (
                Auth.can(
                    this.invoiceType === 'CustomerInvoice' ? 'delete customer invoices' : 'delete supplier invoices'
                ) && !this.event?.is_closed
            );
        },
        isAcumaticaInvoice() {
            return this.invoiceType === 'SupplierInvoice' && this.invoice?.integrator === 'acumatica';
        },
        isCustomerInvoice() {
            return !!this.invoice?.revenue_entry_id;
        },
        isConnectedToXero() {
            return this.store.get('account.is_connected_to_xero');
        },
        isConnectedToAcumatica() {
            return (
                this.store.get('account.is_connected_to_acumatica') && this.store.get('account.integrations.acumatica')
            );
        },
        isPublishedToXero() {
            if (!this.invoice) {
                return false;
            }
            return Boolean(
                this.invoice.invoice_guid &&
                this.isConnectedToXero &&
                this.invoice.view_url &&
                (this.invoiceType === 'CustomerInvoice' || this.invoice.integrator === 'xero')
            );
        },
        isPublishedToAcumatica() {
            return Boolean(
                this.invoiceType === 'SupplierInvoice' &&
                this.invoice?.integrator === 'acumatica' &&
                this.invoice?.invoice_guid &&
                this.acumaticaInvoiceUrl
            );
        },
        acumaticaInvoiceUrl() {
            if (!this.isConnectedToAcumatica || !this.invoice?.invoice_number) {
                return undefined;
            }

            const acumaticaIntegration = this.store.get('account.integrations.acumatica');
            if (!acumaticaIntegration?.oauth_url || !acumaticaIntegration.organisation_name) {
                return undefined;
            }

            const companyId = acumaticaIntegration.organisation_name.replaceAll(' ', '+');

            return `${acumaticaIntegration.oauth_url}/Main?CompanyID=${companyId}&ScreenId=AP301000&DocType=Bill&RefNbr=${this.invoice.invoice_number}`;
        },
        canPublishToXero() {
            return Boolean(
                this.invoice &&
                this.isConnectedToXero &&
                !this.event?.is_closed &&
                (this.invoiceType === 'CustomerInvoice' || (!this.invoice.integrator && !this.invoice.invoice_guid))
            );
        },
        impliedInvoiceValue() {
            if (!this.invoice) {
                return 0;
            }

            const targetValue = parseFloat(this.invoice.total_value_target);
            const conversionRate = parseFloat(this.invoice.conversion_rate);

            if (!Number.isFinite(targetValue) || !Number.isFinite(conversionRate) || conversionRate <= 0) {
                return parseFloat(this.invoice.total_value);
            }

            return targetValue / conversionRate;
        },
        totalOutstanding() {
            if (!this.invoice) {
                return 0;
            }

            return calculateTotalOutstanding(this.invoice);
        },
        visibleTabs() {
            if (!this.invoice) return;
            const tabs = [
                {
                    title: 'Details',
                    name: 'details',
                },
            ];
            if (this.invoice.combined_payments.length) {
                tabs.push({
                    title: 'Payments/Credits',
                    name: 'payments',
                });
            }
            if (this.invoice.activity.length) {
                tabs.push({
                    title: 'Activity',
                    name: 'activity',
                });
            }
            if (this.invoiceType === 'SupplierInvoice') {
                tabs.push({
                    title: 'Purchase Orders',
                    name: 'purchase_orders',
                });
            }
            if (this.invoiceType === 'SupplierInvoice') {
                tabs.push({
                    title: 'Attachments',
                    name: 'attachments',
                });
            }
            return tabs;
        },
    },
    watch: {
        invoice: {
            handler() {
                if (this.isEditable && !this.isLoading && !this.isUpdatingFiles) {
                    this.debouncedUpdateInvoice();
                }
            },
            deep: true,
        },
    },
    unmounted() {
        Eventbus.$off('viewInvoice');
        Eventbus.$off('reloadInvoice');
        Eventbus.$off('reloadSupplierInvoices');
        Eventbus.$off('reloadCustomerInvoices');
    },
    mounted() {
        Eventbus.$on('viewInvoice', ({ type, routeData }) => {
            if (typeof type != 'string') {
                if (Object.keys(type).includes('routeData')) {
                    routeData = type.routeData;
                    type = type.type;
                }
            }
            this.open(type, routeData);
        });
        Eventbus.$on('reloadInvoice', () => {
            this.reload();
        });
        Eventbus.$on('reloadSupplierInvoices', () => {
            if (this.invoice) {
                this.reload();
            }
        });
        Eventbus.$on('reloadCustomerInvoices', () => {
            this.reload();
        });
    },
    methods: {
        formatCurrency,
        subtotal,
        total,
        open(type, routeData) {
            if (!type || !['CustomerInvoice', 'SupplierInvoice'].includes(type)) {
                toast('Error', 'Invalid type specified when opening invoice', 'error');
                return;
            } else if (!routeData) {
                toast('Error', 'Invalid route data specified when opening invoice', 'error');
                return;
            } else if (
                !Auth.can(type === 'CustomerInvoice' ? 'view customer invoices' : 'view supplier invoices') &&
                !this.currentAccount.is_supplier
            ) {
                toast(
                    'Error',
                    `You do not have permission to view ${
                        type === 'CustomerInvoice' ? 'customer' : 'supplier'
                    } invoices`
                );
                return;
            }
            this.$refs.modal?.open();
            this.invoiceType = type;
            this.routeData = routeData;
            this.isLoading = true;
            axios
                .get(
                    type === 'CustomerInvoice'
                        ? route('api.account.event.revenue.invoices.get', routeData)
                        : route('api.account.event.purchaseorders.invoices.get', routeData)
                )
                .then(({ data }) => {
                    this.invoice = data;
                    this.paymentTermsDraft = data.payment_terms || '';
                    if (routeData.openPaymentDates) {
                        this.managePayments();
                    }
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        reload() {
            if (!this.invoice || !this.invoiceType) {
                return;
            }
            this.isLoading = true;
            this.isPublishing = false;
            axios
                .get(
                    this.invoiceType === 'CustomerInvoice'
                        ? route('api.account.event.revenue.invoices.get', {
                              event: this.invoice.event.id,
                              revenueEntry: this.invoice.revenue_entry_id,
                              customerInvoice: this.invoice.id,
                          })
                        : route('api.account.event.purchaseorders.invoices.get', {
                              event: this.invoice.event_id,
                              purchaseOrder: this.invoice.purchase_orders[0].id,
                              supplierInvoice: this.invoice.id,
                          })
                )
                .then(({ data }) => {
                    this.invoice = data;
                    this.paymentTermsDraft = data.payment_terms || '';
                })
                .finally(() => (this.isLoading = false));
        },
        reset() {
            this.isLoading = false;
            this.isPublishing = false;
            this.pushingToXero = {};
            this.invoiceType = null;
            this.invoice = null;
            this.paymentTermsDraft = '';
        },
        close() {
            this.reset();
            Eventbus.$emit(
                this.invoiceType === 'CustomerInvoice' ? 'reloadCustomerInvoices' : 'reloadSupplierInvoices'
            );
            this.$refs.modal.close();
        },
        debouncedUpdateInvoice: debounce(function () {
            this.updateInvoice();
        }, 1500),
        updateInvoice() {
            if (!this.invoice || this.isAcumaticaInvoice) {
                return;
            }
            if (
                !Auth.can(
                    this.invoiceType === 'CustomerInvoice' ? 'update customer invoices' : 'update supplier invoices'
                )
            ) {
                toast(
                    'Error',
                    `You do not have permission to ${
                        this.invoiceType === 'CustomerInvoice' ? 'update' : 'view'
                    } customer invoices`
                );
                return;
            }
            // If invoice.po_number is passed in a Supplier Invoice, validation will strip it out, which is ok.
            const isSupplierInvoice = this.invoiceType === 'SupplierInvoice';
            axios
                .post(
                    this.invoiceType === 'CustomerInvoice'
                        ? route('api.account.event.revenue.invoices.update', {
                              event: this.invoice.event.id,
                              revenueEntry: this.invoice.revenue_entry_id,
                              customerInvoice: this.invoice.id,
                          })
                        : route('api.account.event.purchaseorders.invoices.update', {
                              event: this.invoice.event_id,
                              purchaseOrder: this.invoice.purchase_orders[0].id,
                              supplierInvoice: this.invoice.id,
                          }),
                    this.invoice
                )
                .then(() => {
                    if (isSupplierInvoice) {
                        capturePosthogEvent('supplier_invoice_updated');
                    }
                });
        },
        updateFilesChanged(file) {
            if (!file) {
                return;
            }
            const formData = {
                files: [],
                invoiceId: this.invoice.id,
                purchaseOrderId: this.invoice.purchase_orders[0].id,
            };
            formData.files = file;
            axios
                .post(
                    route('api.account.event.invoices.addUpload', {
                        event: this.invoice.event_id,
                    }),
                    obj2form(formData)
                )
                .then(() => {
                    capturePosthogEvent('supplier_invoice_updated');
                    Eventbus.$emit('reloadInvoice');
                });
        },
        downloadInvoice() {
            capturePosthogEvent('customer_invoice_downloaded');
            window.open(
                route('api.account.event.revenue.invoices.download', {
                    event: this.invoice.event_id,
                    revenueEntry: this.invoice.revenue_entry_id,
                    customerInvoice: this.invoice.id,
                }),
                '_blank'
            );
        },
        deleteInvoice() {
            if (!this.invoice || !this.canDeleteInvoice) {
                return;
            }
            this.$refs.deleteInvoiceModal.open(this.invoice, this.invoiceType);
        },
        managePayments() {
            this.$refs.invoicePaymentDatesModal.openModal(
                this.isCustomerInvoice
                    ? {
                          event: this.invoice.event_id,
                          revenueEntry: this.invoice.revenue_entry_id,
                          invoice: this.invoice.id,
                      }
                    : {
                          event: this.invoice.event_id,
                          purchaseOrder: this.invoice.purchase_orders[0].id,
                          invoice: this.invoice.id,
                          isSupplierRaised: this.invoice.supplier_raised,
                      }
            );
        },
        raiseCreditNote() {
            const eventId = this.isCustomerInvoice ? this.invoice.event_id : this.invoice.event_id;
            Eventbus.$emit('raiseCreditNote', {
                type: this.invoiceType,
                invoice: this.invoice,
                eventId: eventId,
            });
        },
        deleteCreditNote(creditNote) {
            this.isLoading = true;
            axios
                .delete(
                    route('api.account.event.creditNotes.delete', {
                        event: this.invoice.event_id,
                        creditNote: creditNote.id,
                    })
                )
                .then(() => this.reload())
                .finally(() => (this.isLoading = false));
        },
        showEmailInvoiceModal() {
            if (!this.invoice) {
                return;
            }
            Eventbus.$emit('send:email', {
                model: this.invoice,
                type: 'invoice',
            });
        },
        authoriseInvoice() {
            if (!Auth.can('authorise supplier invoices')) {
                toast('Error', 'You do not have permission to authorise supplier invoices', 'error');
                return;
            }
            if (!this.invoice.supplier_raised) {
                toast('Error', 'You cannot authorise a non-supplier raised invoice', 'error');
                return;
            }
            if (this.invoice.status !== 'awaiting_approval') {
                toast('Error', 'You cannot authorise a supplier invoice unless it is awaiting approval', 'error');
                return;
            }

            this.isLoading = true;
            axios
                .post(
                    route('api.account.event.purchaseorders.invoices.authorise', {
                        event: this.invoice.event_id,
                        purchaseOrder: this.invoice.purchase_orders[0].id,
                        supplierInvoice: this.invoice.id,
                    })
                )
                .then(() => {
                    toast(
                        'Success',
                        'The supplier-raised invoice ' + this.invoice.invoice_number + ' has been authorised',
                        'success'
                    );
                    this.reload();
                    Eventbus.$emit('supplierInvoice:authorised');
                    if (this.invoice.supplier_raised) {
                        if (this.canPublishToXero) {
                            this.publishToXero();
                        }
                        this.managePayments();
                    }
                })
                .finally(() => (this.isLoading = false));
        },
        rejectInvoice() {
            if (!Auth.can('authorise supplier invoices')) {
                toast('Error', 'You do not have permission to authorise supplier invoices', 'error');
                return;
            }
            if (!this.invoice.supplier_raised) {
                toast('Error', 'You cannot reject a non-supplier raised invoice', 'error');
                return;
            }
            if (this.invoice.status !== 'awaiting_approval') {
                toast('Error', 'You cannot reject a supplier invoice unless it is awaiting approval', 'error');
                return;
            }

            swal.fire({
                title: 'Reject Supplier Invoice',
                text: 'Please provide a brief reason for rejecting this supplier-raised invoice',
                input: 'text',
                inputAttributes: {
                    class: 'flex-fill',
                    maxlength: '255',
                },
                showCancelButton: true,
            }).then((result) => {
                this.isLoading = true;
                axios
                    .post(
                        route('api.account.event.purchaseorders.invoices.reject', {
                            event: this.invoice.event_id,
                            purchaseOrder: this.invoice.purchase_orders[0].id,
                            supplierInvoice: this.invoice.id,
                        }),
                        {
                            comments: result.value ?? null,
                        }
                    )
                    .then(() => {
                        toast(
                            'Success',
                            'The supplier-raised invoice ' + this.invoice.invoice_number + ' has been rejected',
                            'success'
                        );
                        this.reload();
                    })
                    .finally(() => {
                        this.isLoading = false;
                        Eventbus.$emit('reloadSupplierInvoices');
                    });
            });
        },
        alterCurrency() {
            this.$refs.alterCurrencyModal.open(
                this.invoice.base_currency,
                this.invoice.target_currency,
                this.invoice.conversion_rate,
                this.invoice.total_value
            );
        },
        updateCurrency(currency) {
            if (!this.isEditable) {
                return;
            }
            this.invoice.target_currency = currency.target;
            this.invoice.conversion_rate = currency.rate;
            this.updateInvoice();
        },
        resyncFromXero() {
            if (!this.invoice) {
                return;
            }
            this.isResyncing = true;
            axios
                .post(
                    this.invoiceType === 'CustomerInvoice'
                        ? route('api.account.event.revenue.invoices.resyncFromXero', {
                              event: this.invoice.event_id,
                              revenueEntry: this.invoice.revenue_entry_id,
                              customerInvoice: this.invoice.id,
                          })
                        : route('api.account.event.purchaseorders.invoices.resyncFromXero', {
                              event: this.invoice.event_id,
                              purchaseOrder: this.invoice.purchase_orders[0].id,
                              supplierInvoice: this.invoice.id,
                          })
                )
                .then(() => {
                    toast('Success', 'The invoice will be resynced from Xero within a few moments', 'success');
                })
                .finally(() => {
                    this.isResyncing = false;
                });
        },
        publishToXero() {
            if (!this.invoice || (!this.isPublishedToXero && !this.canPublishToXero)) {
                return;
            }
            if (this.invoiceType === 'SupplierInvoice') {
                capturePosthogEvent('supplier_invoice_published_to_xero');
            } else if (this.invoiceType === 'CustomerInvoice') {
                capturePosthogEvent('customer_invoice_published_to_xero');
            }
            this.isPublishing = true;
            axios
                .post(
                    this.invoiceType === 'CustomerInvoice'
                        ? route('api.account.event.revenue.invoices.pushToXero', {
                              event: this.invoice.event_id,
                              revenueEntry: this.invoice.revenue_entry_id,
                              customerInvoice: this.invoice.id,
                          })
                        : route('api.account.event.purchaseorders.invoices.pushToXero', {
                              event: this.invoice.event_id,
                              purchaseOrder: this.invoice.purchase_orders[0].id,
                              supplierInvoice: this.invoice.id,
                          })
                )
                .then(() => {
                    toast('Success', 'The invoice should be published to Xero within a few moments', 'success');
                });
        },
        viewPurchaseOrder(purchaseOrder) {
            Eventbus.$emit('purchaseOrder:view', {
                purchaseOrder: purchaseOrder.id,
                event: purchaseOrder.event_id,
                source: {
                    emitter: 'viewInvoice',
                    payload: {
                        type: 'SupplierInvoice',
                        routeData: this.routeData,
                    },
                },
            });
            this.close();
        },
        async removeAttachment(index) {
            const fileUploadId = this.invoice.file_uploads[index].id;
            this.isUpdatingFiles = true;
            try {
                await axios.delete(route('api.account.files.delete', { fileUpload: fileUploadId }));
                capturePosthogEvent('supplier_invoice_updated');
                this.invoice.file_uploads.splice(index, 1);
                await this.$nextTick();
            } finally {
                this.isUpdatingFiles = false;
            }
        },
        fileXeroIntegration(file) {
            return (file.file_upload_integrations ?? []).find(
                (integration) => integration.integrator === 'xero' && !integration.deleted_at
            );
        },
        pushAttachmentToXero(file) {
            this.pushingToXero[file.id] = true;
            axios.post(route('api.account.files.pushToXero', { fileUpload: file.id })).then(() => {
                toast(
                    'The file is being pushed to Xero.',
                    'It should appear in Xero soon. Please allow some time for this change to appear in Eventwise',
                    'success'
                );
            });
        },
        openAttachment(file) {
            if (!file.download_url) {
                return;
            }
            window.open(file.download_url, '_blank');
        },
    },
};
</script>
<style lang="scss">
@use '@/assets/sass/eventbooks/variables' as variables;

.view-invoice-content {
    .invoice-heading {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
    }
    .pad-x {
        padding: 0 15px;
    }

    .invoice-line-items-table {
        width: 100%;
        vertical-align: middle;

        thead > tr > th {
            @apply tw-border-b tw-py-2 tw-content-center;
        }

        thead > tr > th,
        tfoot > tr > th {
            @apply tw-px-3;
            text-transform: uppercase;
            font-family: variables.$eventwise-font-heading;
            font-size: 0.9em;
            opacity: 0.5;
        }

        tbody {
            & > tr:not(:last-of-type) {
                border-bottom: 1px solid rgba(black, 0.1);
            }
            & > tr > td {
                @apply tw-px-3 tw-py-1;
            }
        }

        tfoot {
            & > tr:first-of-type {
                border-top: 1px solid rgba(black, 0.1);
            }
        }

        tfoot > tr > td.tw-text-right,
        tfoot > tr > th.tw-text-right {
            @apply tw-pr-3;
        }
    }
}
</style>
